import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import AsyncSelect from "react-select/async";
import Select from 'react-select';
import { Check, ChevronDown, Plus, Search, Trash2, Users, Truck, Package, Layers, Settings, Loader2, X, Clock, Edit2 } from 'lucide-react';
import api from '../api';
import { fetchErpagCategories, fetchErpagWarehouses } from '../api/erpag';
import { getDetailedSkuProducts, searchSkuProducts, saveSkuProducts } from '../api/ProductsApis';
import GovernanceLoader from './GovernanceLoader';

const FORMAT_TYPE_OPTIONS = ["Bulk", "Retail", "Shipping", "Trees", "Services"];

// --- Helper Functions ---
const getCategoryName = (category) => {
  if (typeof category === 'string') return category.trim();
  if (!category || typeof category !== 'object') return '';
  return String(category.name || category.category || category.categoryName || category.category_name || category.erpCategory || category.erp_category || category.CategoryName || category.Category || category['Category Name'] || category['category name'] || category.NAME || category.title || '').trim();
};

const getCustomerName = (customer) => {
  if (typeof customer === 'string') return customer.trim();
  if (!customer || typeof customer !== 'object') return '';
  return String(customer.customer || customer.name || customer.customerName || customer.customer_name || customer.Customer || customer.CustomerName || '').trim();
};

const getProductSku = (product) => String(product?.sku || product?.SKU || product?.productSku || product?.product_sku || product?.itemSku || '').trim();

const getFamilySkuValue = (entry) => String(typeof entry === 'string' ? entry : entry?.sku || entry?.SKU || entry?.productSku || entry?.product_sku || '').trim();

const getFamilySkuKey = (entry) => getFamilySkuValue(entry).toLowerCase();

const getUniqueFamilySkus = (skus) => {
  const seen = new Set();
  return (Array.isArray(skus) ? skus : []).reduce((uniqueSkus, entry) => {
    const sku = getFamilySkuValue(entry);
    const key = sku.toLowerCase();
    if (!sku || seen.has(key)) return uniqueSkus;
    seen.add(key);
    uniqueSkus.push(sku);
    return uniqueSkus;
  }, []);
};

// const parseSkuList = (value) => String(value || "").split(/[\s,;]+/).map((sku) => sku.trim()).filter(Boolean);
// const getProductTitle = (product) => String(product?.skuTitle || product?.title || product?.description || product?.descriptionEn || product?.nameEn || product?.productName || product?.product_name || '').trim();
// const getProductCategory = (product) => String(product?.category || product?.itemCategory || product?.erpCategory || product?.erp_category || product?.productCategory || product?.customFields?.category || product?.customFields?.itemCategory || '').trim();

const getProductFormat = (product) => String(product?.format || product?.formatFamily || product?.format_family || product?.formatType || product?.packaging || product?.packing || '').trim();

const normalizeFormatType = (value) => {
  const cleanValue = String(value || '').trim().toLowerCase();
  if (cleanValue === 'retail') return 'Retail';
  if (cleanValue === 'bulk') return 'Bulk';
  if (cleanValue === 'shipping') return 'Shipping';
  if (cleanValue === 'trees') return 'Trees';
  if (cleanValue === 'services') return 'Services';
  return '';
};

const getMappingFamily = (mapping) => String(mapping?.categoryFamily || mapping?.format || '').trim();
const getMappingFormatType = (mapping) => normalizeFormatType(mapping?.formatType);

// Coerce string "true"/"false"/boolean/1/0 into a real boolean
const toBool = (v) => {
  if (v === true || v === 1 || v === '1') return true;
  if (v === false || v === 0 || v === '0' || v === null || v === undefined || v === '') return false;
  if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
  return Boolean(v);
};

const normalizeRouteRow = (row) => ({
  ...row,
  hasCancelDate: toBool(row.hasCancelDate),
  requiresDeliveryAppointment: toBool(row.requiresDeliveryAppointment),
  requiresCustomsDocs: toBool(row.requiresCustomsDocs),
  leadTime: row.leadTime === '' || row.leadTime === null || row.leadTime === undefined ? null : Number(row.leadTime),
});

// --- Styles ---
const categorySelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    height: '38px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
  }),
  valueContainer: (provided) => ({ ...provided, height: '38px', padding: '0 12px' }),
  input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
  indicatorsContainer: (provided) => ({ ...provided, height: '38px' }),
  menu: (provided) => ({ ...provided, zIndex: 50 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected || state.isFocused ? '#3b82f6' : '#ffffff',
    color: state.isSelected || state.isFocused ? '#ffffff' : '#111827',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
};

const customerMultiSelectStyles = {
  ...categorySelectStyles,
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
  }),
  valueContainer: (provided) => ({ ...provided, minHeight: '38px', padding: '2px 8px' }),
  multiValue: (provided) => ({ ...provided, backgroundColor: '#eef2ff', borderRadius: '0.375rem' }),
  multiValueLabel: (provided) => ({ ...provided, color: '#3730a3', fontSize: '0.8125rem' }),
};

const CheckboxOption = (props) => {
  const { isDisabled, label, isSelected, innerProps, innerRef, data } = props;
  const isInFamily = data?.isInFamily || false;
  const isChecked = isSelected;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        ...innerProps.style,
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        backgroundColor: isDisabled ? "#f9fafb" : "white",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={isChecked}
          disabled={isDisabled}
          readOnly
          style={{ marginRight: "8px", cursor: isDisabled ? "not-allowed" : "pointer" }}
        />
        <span style={{ color: isDisabled ? "#9ca3af" : "#111827" }}>{label}</span>
      </div>
      {isInFamily && (
        <span
          style={{
            fontSize: "10px",
            fontWeight: 600,
            color: "#059669",
            backgroundColor: "#d1fae5",
            padding: "2px 8px",
            borderRadius: "9999px",
            marginLeft: "8px",
            whiteSpace: "nowrap",
          }}
        >
          Assigned
        </span>
      )}
    </div>
  );
};

export default function AdminSettings() {
  const API_URL = `${(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')}/api/analytics/admin`;
  const [activeTab, setActiveTab] = useState('customers');

  // Loaders
  const [pageLoading, setPageLoading] = useState(true);
  const [isPfLoading, setIsPfLoading] = useState(false);
  //const [isProductLoading, setIsProductLoading] = useState(false);
  const [savingFamilyId, setSavingFamilyId] = useState(null);

  // Data States
  const [customerFamilies, setCustomerFamilies] = useState([]);
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [productFamilies, setProductFamilies] = useState([]);
  const [totalProductFamilies, setTotalProductFamilies] = useState(0);
  const [detailedSkuProducts, setDetailedSkuProducts] = useState([]);
  //const [detailedSkuLoading, setDetailedSkuLoading] = useState(false);
  const [detailedSkuError, setDetailedSkuError] = useState("");
  const [formats, setFormats] = useState([]);
  const [activeCustomers, setActiveCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  //const [customersError, setCustomersError] = useState("");
  const [erpCategories, setErpCategories] = useState([]);
  //const [categoriesLoading, setCategoriesLoading] = useState(false);
  //const [categoriesError, setCategoriesError] = useState("");

  // Pagination & Search States
  const [newFamilyName, setNewFamilyName] = useState("");
  //const [newItemName, setNewItemName] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeFamilyId, setActiveFamilyId] = useState(null);
  const [expandedProductFamilyId, setExpandedProductFamilyId] = useState(null);

  const [productFamilySearch, setProductFamilySearch] = useState("");
  const [productFamilyPage, setProductFamilyPage] = useState(1);
  const [productFamilyPageSize, setProductFamilyPageSize] = useState(10);

  // Format Pagination States
  const [formatPage, setFormatPage] = useState(1);
  const [formatPageSize, setFormatPageSize] = useState(20);

  // Format states
  const [formatDrafts, setFormatDrafts] = useState({});
  const [savingFormatCategory, setSavingFormatCategory] = useState("");
  //const [selectedFormatCategories, setSelectedFormatCategories] = useState([]);

  const [searchTerms, setSearchTerms] = React.useState({});
  const [skuSearchTerms, setSkuSearchTerms] = React.useState({});

  // --- DYNAMIC REGIONS STATE LOADED FROM DATABASE ---
  const [regions, setRegions] = useState([]);
  const [editingRegionId, setEditingRegionId] = useState(null);
  const [editingRegionName, setEditingRegionName] = useState('');
  const [newRegionName, setNewRegionName] = useState('');

  // Dynamically constructed options for dropdowns
  const dynamicRegionOptions = useMemo(
    () => regions.map(r => ({ label: r.name, value: r.name, id: r._id || r.id })),
    [regions]
  );

  // --- Transit Times Management States (INDIVIDUAL ITEM CRUD) ---
  const [transitLevels, setTransitLevels] = useState([]);
  const [newTransitLevelName, setNewTransitLevelName] = useState('');
  const [newTransitDays, setNewTransitDays] = useState('');
  const [savingTransitId, setSavingTransitId] = useState(null);

  // Fetch Paged Product Families
  const fetchPagedProductFamilies = useCallback(async () => {
    try {
      setIsPfLoading(true);
      const params = new URLSearchParams({
        page: productFamilyPage,
        limit: productFamilyPageSize,
        search: productFamilySearch
      });

      const res = await fetch(`${API_URL}/product-families?${params}`);
      const json = await res.json();

      if (json.success) {
        setProductFamilies(json.data);
        setTotalProductFamilies(json.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch product families", error);
    } finally {
      setIsPfLoading(false);
    }
  }, [productFamilyPage, productFamilyPageSize, productFamilySearch, API_URL]);

  useEffect(() => {
    fetchPagedProductFamilies();
  }, [fetchPagedProductFamilies]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setPageLoading(true);
      // setCategoriesLoading(true);
      setCustomersLoading(true);
      // setDetailedSkuLoading(true);

      const [cfRes, fmRes, erpCategoryRes, customersRes, detailedSkuRes, assignedRoutesRes, transitRes, regionsRes] = await Promise.all([
        fetch(`${API_URL}/families`).then(res => res.json()).catch(() => ({ success: false })),
        fetch(`${API_URL}/formats`).then(res => res.json()).catch(() => ({ success: false })),
        fetchErpagCategories(),
        api.get('/api/analytics/customers?limit=-1').then((res) => ({ success: true, data: res.data })).catch(e => ({ success: false, error: e.message, data: [] })),
        getDetailedSkuProducts().then((data) => ({ success: true, data })).catch(e => ({ success: false, error: e.message, data: { products: [] } })),
        fetch(`${API_URL}/assigned-routes`).then(res => res.json()).catch(() => ({ success: false })),
        fetch(`${API_URL}/transit-times`).then(res => res.json()).catch(() => ({ success: false })),
        fetch(`${API_URL}/regions`).then(res => res.json()).catch(() => ({ success: false })),
      ]);

      if (cfRes?.success) setCustomerFamilies(cfRes.data);
      if (assignedRoutesRes?.success) setAssignedRoutes((assignedRoutesRes.data || []).map(normalizeRouteRow));
      if (fmRes?.success) setFormats(fmRes.data);

      // Load Dynamic Regions from DB
      if (regionsRes?.success && Array.isArray(regionsRes.data)) {
        setRegions(regionsRes.data);
      }

      // Load Transit Times from DB with normalized id
      if (transitRes?.success && Array.isArray(transitRes.data) && transitRes.data.length > 0) {
        setTransitLevels(transitRes.data.map(tl => ({ ...tl, id: tl._id || tl.id })));
      }

      if (detailedSkuRes?.success) {
        setDetailedSkuProducts(Array.isArray(detailedSkuRes.data?.products) ? detailedSkuRes.data.products : []);
      } else {
        setDetailedSkuError(detailedSkuRes?.error || '');
      }

      if (customersRes?.success) {
        const names = Array.from(new Set(customersRes.data.map(getCustomerName).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        setActiveCustomers(names);
      }

      if (erpCategoryRes?.success) {
        const catNames = Array.from(new Set(erpCategoryRes.data.map(getCategoryName).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        setErpCategories(catNames);
      }
    } catch (error) {
      console.error("Initial load error", error);
    } finally {
      // setCategoriesLoading(false);
      setCustomersLoading(false);
      //setDetailedSkuLoading(false);
      setPageLoading(false);
    }
  };

  const activeCustomerOptions = useMemo(
    () => activeCustomers.map(name => ({ value: name, label: name })),
    [activeCustomers]
  );

  // --- Logic Handlers ---
  const searchProductOptions = async (inputValue) => {
    if (!inputValue.trim()) return [];
    try {
      const { products } = await searchSkuProducts(inputValue);
      return products.map((p) => ({
        value: p.sku,
        label: `${p.sku} - ${p.description}`,
        isAssigned: p.isAssigned,
        ...p,
      }));
    } catch (e) { return []; }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const lastSearchRef = useRef("");

  const searchCustomerOptions = async (inputValue) => {
    try {
      const res = await api.get(`/api/analytics/customers?q=${inputValue}&limit=150`);
      return res.data.map((c) => {
        const option = {
          value: c.customerName,
          label: c.customerName,
          key: c.key,
          isInFamily: c.isInFamily === true,
        };
        return option;
      });
    } catch (e) {
      console.error("Error searching customers:", e);
      return [];
    }
  };

  const handleAddCustomerFamily = async () => {
    if (!newFamilyName) return;
    try {
      const res = await fetch(`${API_URL}/families`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFamilyName, customers: [] })
      });
      const json = await res.json();
      if (json.success) setCustomerFamilies([...customerFamilies, json.data]);
      setNewFamilyName("");
    } catch (error) { console.error(error); }
  };

  const handleAddCustomerToFamily = async (familyId) => {
    if (selectedCustomers.length === 0) return;
    setSavingFamilyId(familyId);
    try {
      const family = customerFamilies.find(f => f._id === familyId);
      const selectedCustomerNames = selectedCustomers.map((o) => o.value).filter(Boolean);
      const newCustomers = Array.from(new Set([...(family.customers || []), ...selectedCustomerNames]));
      const res = await fetch(`${API_URL}/families/${familyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: family.name, customers: newCustomers })
      });
      const json = await res.json();
      if (json.success) setCustomerFamilies(customerFamilies.map(f => f._id === familyId ? json.data : f));

      setSelectedCustomers([]);
      setActiveFamilyId(null);
      setSearchQuery("");
      lastSearchRef.current = "";
    } catch (e) {
      console.error(e);
    } finally {
      setSavingFamilyId(null);
    }
  };

  const handleRemoveCustomer = async (familyId, customerName) => {
    if (!window.confirm(`Are you sure you want to remove ${customerName} from this family?`)) return;
    setPageLoading(true);
    try {
      const family = customerFamilies.find(f => f._id === familyId);
      const newCustomers = family.customers.filter(c => c !== customerName);
      const res = await fetch(`${API_URL}/families/${familyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: family.name, customers: newCustomers })
      });
      const json = await res.json();
      if (json.success) setCustomerFamilies(customerFamilies.map(f => f._id === familyId ? json.data : f));
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  const handleDeleteCustomerFamily = async (familyId) => {
    if (!window.confirm("Are you sure you want to delete this customer family?")) return;
    setPageLoading(true);
    try {
      const res = await fetch(`${API_URL}/families/${familyId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) setCustomerFamilies(customerFamilies.filter(f => f._id !== familyId));
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  const handleAddProductFamily = async () => {
    if (!newFamilyName) return;
    setPageLoading(true);
    try {
      const res = await fetch(`${API_URL}/product-families`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFamilyName, skus: [] })
      });
      const json = await res.json();
      if (json.success) {
        setProductFamilies([...productFamilies, json.data]);
        fetchPagedProductFamilies();
      }
      setNewFamilyName("");
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  const handleAddSkuToFamily = async (familyId) => {
    const selectedSkus = selectedProducts.map((o) => o.value).filter(Boolean);
    if (selectedSkus.length === 0) return;
    setPageLoading(true);
    try {
      const family = productFamilies.find(f => f._id === familyId);
      const currentSkus = getUniqueFamilySkus(family.skus);
      const newSkus = getUniqueFamilySkus([...currentSkus, ...selectedSkus]);
      const json = await saveSkuProducts(newSkus, familyId, family);
      if (json.success) {
        setProductFamilies(productFamilies.map(f => f._id === familyId ? json.data : f));
      }
      setSelectedProducts([]);
      setActiveFamilyId(null);
      setExpandedProductFamilyId(familyId);
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  const handleRemoveSku = async (familyId, sku) => {
    if (!window.confirm(`Are you sure you want to remove SKU ${sku} from this family?`)) return;
    setPageLoading(true);
    try {
      const family = productFamilies.find(f => f._id === familyId);
      const skuKey = getFamilySkuKey(sku);
      const newSkus = getUniqueFamilySkus(family.skus).filter(s => getFamilySkuKey(s) !== skuKey);
      const new_URL = `${(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')}`;
      const res = await fetch(`${new_URL}/api/products/remove-skus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: familyId, name: family.name, skus: newSkus }),
      });
      const json = await res.json();
      if (json.success) setProductFamilies(productFamilies.map(f => f._id === familyId ? json.data : f));
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  const handleDeleteProductFamily = async (familyId) => {
    if (!window.confirm("Are you sure you want to delete this product family?")) return;
    setPageLoading(true);
    try {
      const res = await fetch(`${API_URL}/product-families/${familyId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        fetchPagedProductFamilies();
        if (expandedProductFamilyId === familyId) setExpandedProductFamilyId(null);
      }
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  // --- Format Handlers ---
  const handleFormatDraftChange = (category, field, value) => {
    const key = String(category || '').trim().toLowerCase();
    setFormatDrafts(c => ({ ...c, [key]: { ...c[key], [field]: value } }));
  };

  const handleSaveFormatMapping = async (category) => {
    setPageLoading(true);
    const key = category.toLowerCase();
    const existingMapping = formatRecordByErpCategory.get(key);
    const draft = formatDrafts[key] || {};

    const categoryFamily = draft.categoryFamily !== undefined ? draft.categoryFamily : (getMappingFamily(existingMapping) || '');
    const formatType = draft.formatType !== undefined ? draft.formatType : (getMappingFormatType(existingMapping) || '');

    try {
      setSavingFormatCategory(key);
      const res = await fetch(existingMapping?._id ? `${API_URL}/formats/${existingMapping._id}` : `${API_URL}/formats`, {
        method: existingMapping?._id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          erpCategory: category,
          categoryFamily: categoryFamily,
          formatType: formatType,
          format: categoryFamily
        })
      });
      const json = await res.json();
      if (json.success) {
        setFormats(c => [...c.filter(f => String(f.erpCategory).toLowerCase() !== key), json.data]);
        setFormatDrafts(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    } catch (e) { console.error(e); }
    finally { setPageLoading(false); setSavingFormatCategory(""); }
  };

  const handleDeleteFormat = async (id, cat) => {
    if (!window.confirm(`Are you sure you want to delete the mapping for ${cat}?`)) return;
    setPageLoading(true);
    try {
      await fetch(`${API_URL}/formats/${id}`, { method: 'DELETE' });
      setFormats(formats.filter(f => f._id !== id));
    } catch (e) { console.error(e); }
    setPageLoading(false);
  };

  // --- Memos ---
  const detailedSkuBySku = useMemo(() => {
    return detailedSkuProducts.reduce((map, product) => {
      const sku = getProductSku(product);
      if (sku) map.set(sku.toLowerCase(), product);
      return map;
    }, new Map());
  }, [detailedSkuProducts]);

  const formatByErpCategory = useMemo(() => {
    return formats.reduce((map, row) => {
      const category = String(row.erpCategory || '').trim().toLowerCase();
      if (category) map.set(category, row.format || '');
      return map;
    }, new Map());
  }, [formats]);

  const formatRecordByErpCategory = useMemo(() => {
    return formats.reduce((map, row) => {
      const category = String(row.erpCategory || '').trim().toLowerCase();
      if (category) map.set(category, row);
      return map;
    }, new Map());
  }, [formats]);

  const paginatedProductFamilyRows = useMemo(() => {
    return productFamilies.map((family) => {
      const skus = getUniqueFamilySkus(family.skus);
      const skuRows = skus.map((sku) => {
        const originalSkuEntry = family.skus.find(s => String(s.sku || s) === String(sku));
        const product = detailedSkuBySku.get(String(sku).trim().toLowerCase());
        const category = originalSkuEntry?.category || "N/A";
        const mappedFormat = category ? formatByErpCategory.get(category.toLowerCase()) : "N/A";
        return {
          sku,
          description: originalSkuEntry?.description || "N/A",
          category: category,
          c_family: originalSkuEntry?.c_family || "N/A",
          c_format: mappedFormat || getProductFormat(product),
          isMissingDetail: !product,
        };
      });
      return { ...family, skuCount: skus.length, skuRows };
    });
  }, [productFamilies, detailedSkuBySku, formatByErpCategory]);

  const formatTableRows = useMemo(() => {
    const categoryMap = new Map();
    erpCategories.forEach(c => categoryMap.set(c.toLowerCase(), c));
    formats.forEach(f => categoryMap.set(String(f.erpCategory).toLowerCase(), f.erpCategory));
    return Array.from(categoryMap.values()).sort((a,b)=>a.localeCompare(b)).map(cat => {
      const key = cat.toLowerCase();
      const saved = formatRecordByErpCategory.get(key);
      const draft = formatDrafts[key] || { categoryFamily: getMappingFamily(saved), formatType: getMappingFormatType(saved) };
      return { category: cat, key, savedMapping: saved, draftFamily: draft.categoryFamily, draftFormatType: draft.formatType, hasSavedMapping: !!saved?._id, isDirty: draft.categoryFamily !== getMappingFamily(saved) || draft.formatType !== getMappingFormatType(saved) };
    });
  }, [erpCategories, formats, formatDrafts, formatRecordByErpCategory]);

  const productFamilyPageCount = Math.max(1, Math.ceil(totalProductFamilies / productFamilyPageSize));
  const productFamilyPageStart = (productFamilyPage - 1) * productFamilyPageSize;
  const productFamilyPageEnd = productFamilyPageStart + productFamilies.length;

  const [searchTerm, setSearchTerm] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');

  const filteredFormatRows = useMemo(() => {
    return formatTableRows.filter(row => {
      const matchesSearch = row.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = !typeFilter || row.draftFormatType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [formatTableRows, searchTerm, typeFilter]);

  useEffect(() => {
    setFormatPage(1);
  }, [searchTerm, typeFilter]);

  const formatTotalCount = filteredFormatRows.length;
  const formatPageCount = Math.max(1, Math.ceil(formatTotalCount / formatPageSize));
  const formatPageStart = (formatPage - 1) * formatPageSize;
  const formatPageEnd = Math.min(formatPageStart + formatPageSize, formatTotalCount);

  const paginatedFormatRows = useMemo(() => {
    return filteredFormatRows.slice(formatPageStart, formatPageEnd);
  }, [filteredFormatRows, formatPageStart, formatPageEnd]);

  // --- Route Assignment States & Logic ---
  const [routeAssignmentType, setRouteAssignmentType] = useState('family');
  const [selectedRouteTargets, setSelectedRouteTargets] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isSavingRoute, setIsSavingRoute] = useState(false);

  const [routeLeadTime, setRouteLeadTime] = useState('');
  const [routeWarehouse, setRouteWarehouse] = useState(null);
  const [routeHasCancelDate, setRouteHasCancelDate] = useState(false);
  const [routeRequiresAppointment, setRouteRequiresAppointment] = useState(false);
  const [routeRequiresCustoms, setRouteRequiresCustoms] = useState(false);

  const [erpWarehouses, setErpWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  const [savingCellKey, setSavingCellKey] = useState(null);
  const [leadTimeDrafts, setLeadTimeDrafts] = useState({});

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkRouteValue, setBulkRouteValue] = useState('');
  const [bulkLeadTimeValue, setBulkLeadTimeValue] = useState('');
  const [routeDeliveryRegion, setRouteDeliveryRegion] = useState(null);
  const [bulkDeliveryRegionValue, setBulkDeliveryRegionValue] = useState('');
  const [bulkWarehouseValue, setBulkWarehouseValue] = useState('');
  const [bulkHasCancelDate, setBulkHasCancelDate] = useState('');
  const [bulkRequiresAppointment, setBulkRequiresAppointment] = useState('');
  const [bulkRequiresCustoms, setBulkRequiresCustoms] = useState('');
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  const [isDeletingRow, setIsDeletingRow] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const ROUTE_OPTIONS = [
    { value: 'PICKUP', label: 'Pickup' },
    { value: 'ASA SHIP', label: 'ASA Ship' },
    { value: 'ROUTE LACHINE', label: 'Route Lachine' },
    { value: 'ROUTE BAY', label: 'Route Bay' },
    { value: 'SHIP CLIENT PORTAL', label: 'Client Portal' }
  ];

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setWarehousesLoading(true);
        const res = await fetchErpagWarehouses();
        if (res?.success && Array.isArray(res.data)) {
          setErpWarehouses(res.data);
        }
      } catch (err) {
        console.error('Failed to load ERPAG warehouses', err);
      } finally {
        setWarehousesLoading(false);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = useMemo(
    () => erpWarehouses.map(w => ({
      value: String(w.id ?? w.key),
      label: String(w.description ?? w.id)
    })),
    [erpWarehouses]
  );

  const routeTargetOptions = useMemo(() => {
    if (routeAssignmentType === 'family') {
      return customerFamilies.map(f => ({ value: f.name, label: f.name }));
    }
    return [];
  }, [routeAssignmentType, customerFamilies]);

  const resetRouteForm = () => {
    setSelectedRouteTargets([]);
    setSelectedRoute(null);
    setRouteLeadTime('');
    setRouteDeliveryRegion(null);
    setBulkDeliveryRegionValue('');
    setRouteWarehouse(null);
    setRouteHasCancelDate(false);
    setRouteRequiresAppointment(false);
    setRouteRequiresCustoms(false);
  };

  const resetBulkDrafts = () => {
    setBulkRouteValue('');
    setBulkLeadTimeValue('');
    setBulkWarehouseValue('');
    setBulkHasCancelDate('');
    setBulkRequiresAppointment('');
    setBulkRequiresCustoms('');
  };

  const hasBulkChanges = Boolean(
    bulkRouteValue ||
    bulkLeadTimeValue !== '' ||
    bulkDeliveryRegionValue ||
    bulkWarehouseValue ||
    bulkHasCancelDate !== '' ||
    bulkRequiresAppointment !== '' ||
    bulkRequiresCustoms !== ''
  );

  const handleSaveRouteAssignment = async () => {
    if (!selectedRouteTargets.length || !selectedRoute) return;
    setIsSavingRoute(true);
    try {
      const basePayload = {
        assignType: routeAssignmentType,
        routeName: selectedRoute.value,
        leadTime: routeLeadTime === '' ? null : Number(routeLeadTime),
        deliveryRegion: routeDeliveryRegion ? routeDeliveryRegion.value : null,
        warehouse: routeWarehouse ? routeWarehouse.value : null,
        warehouseLabel: routeWarehouse ? routeWarehouse.label : null,
        hasCancelDate: !!routeHasCancelDate,
        requiresDeliveryAppointment: !!routeRequiresAppointment,
        requiresCustomsDocs: !!routeRequiresCustoms,
      };

      const results = await Promise.all(
        selectedRouteTargets.map(target =>
          fetch(`${API_URL}/assign-route`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...basePayload, targetName: target.value })
          }).then(r => r.json()).catch(err => ({ success: false, error: err.message }))
        )
      );

      const failed = results.filter(r => !r.success);
      if (failed.length) {
        alert(`${failed.length} of ${results.length} assignments failed.`);
      }

      const refreshed = await fetch(`${API_URL}/assigned-routes`).then(r => r.json());
      if (refreshed.success) setAssignedRoutes((refreshed.data || []).map(normalizeRouteRow));

      resetRouteForm();
    } catch (err) {
      console.error(err);
      alert('An error occurred while saving the routes.');
    } finally {
      setIsSavingRoute(false);
    }
  };

  const persistRowUpdate = async (item, patch) => {
    const whObj = patch.warehouse !== undefined
      ? warehouseOptions.find(o => o.value === String(patch.warehouse))
      : null;

    const payload = {
      assignType: item.assignType || 'family',
      targetName: item.name,
      routeName: patch.routeName !== undefined ? patch.routeName : (item.routeName || ''),
      deliveryRegion: patch.deliveryRegion !== undefined ? (patch.deliveryRegion || null) : (item.deliveryRegion || null),
      leadTime: patch.leadTime !== undefined
        ? (patch.leadTime === '' || patch.leadTime === null ? null : Number(patch.leadTime))
        : (item.leadTime === '' || item.leadTime === null || item.leadTime === undefined ? null : Number(item.leadTime)),
      warehouse: patch.warehouse !== undefined ? (patch.warehouse || null) : (item.warehouse || null),
      warehouseLabel: patch.warehouse !== undefined
        ? (whObj ? whObj.label : null)
        : (item.warehouseLabel || null),
      hasCancelDate: toBool(patch.hasCancelDate !== undefined ? patch.hasCancelDate : item.hasCancelDate),
      requiresDeliveryAppointment: toBool(patch.requiresDeliveryAppointment !== undefined ? patch.requiresDeliveryAppointment : item.requiresDeliveryAppointment),
      requiresCustomsDocs: toBool(patch.requiresCustomsDocs !== undefined ? patch.requiresCustomsDocs : item.requiresCustomsDocs),
    };

    const res = await fetch(`${API_URL}/assign-route`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message || 'Update failed');

    setAssignedRoutes(prev => prev.map(r =>
      r._id === item._id
        ? {
            ...r,
            routeName: payload.routeName,
            leadTime: payload.leadTime,
            deliveryRegion: payload.deliveryRegion,
            warehouse: payload.warehouse,
            warehouseLabel: payload.warehouseLabel,
            hasCancelDate: payload.hasCancelDate,
            requiresDeliveryAppointment: payload.requiresDeliveryAppointment,
            requiresCustomsDocs: payload.requiresCustomsDocs
          }
        : r
    ));
    return json;
  };

  const handleCellUpdate = async (item, field, value) => {
    const cellKey = `${item._id}:${field}`;
    setSavingCellKey(cellKey);
    try {
      await persistRowUpdate(item, { [field]: value });
    } catch (err) {
      console.error(err);
      alert('Failed to update: ' + err.message);
    } finally {
      setSavingCellKey(null);
    }
  };

  const handleDeleteAssignedRoute = async (item) => {
    if (!window.confirm(`Delete route assignment for "${item.name}"?`)) return;
    setIsDeletingRow(item._id);
    try {
      const res = await fetch(`${API_URL}/assigned-routes/${item._id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setAssignedRoutes(prev => prev.filter(r => r._id !== item._id));
        setSelectedRowIds(prev => prev.filter(id => id !== item._id));
      } else {
        alert('Delete failed: ' + (json.message || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting row');
    } finally {
      setIsDeletingRow(null);
    }
  };

  const toggleRowSelection = (id) => {
    setSelectedRowIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRowIds.length === assignedRoutes.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(assignedRoutes.map(r => r._id));
    }
  };

  const applyBulkChanges = async () => {
    if (!selectedRowIds.length) return;
    if (!hasBulkChanges) {
      alert('Set at least one bulk value in the column headers first.');
      return;
    }

    const patch = {};
    if (bulkRouteValue) patch.routeName = bulkRouteValue;
    if (bulkLeadTimeValue !== '') patch.leadTime = bulkLeadTimeValue;
    if (bulkDeliveryRegionValue !== '') patch.deliveryRegion = bulkDeliveryRegionValue;
    if (bulkWarehouseValue) patch.warehouse = bulkWarehouseValue;
    if (bulkHasCancelDate !== '') patch.hasCancelDate = bulkHasCancelDate === 'true';
    if (bulkRequiresAppointment !== '') patch.requiresDeliveryAppointment = bulkRequiresAppointment === 'true';
    if (bulkRequiresCustoms !== '') patch.requiresCustomsDocs = bulkRequiresCustoms === 'true';

    const fieldList = Object.keys(patch).join(', ');
    if (!window.confirm(`Apply bulk changes (${fieldList}) to ${selectedRowIds.length} selected row(s)?`)) return;

    setIsBulkApplying(true);
    try {
      const targets = assignedRoutes.filter(r => selectedRowIds.includes(r._id));
      const results = await Promise.all(
        targets.map(item =>
          persistRowUpdate(item, patch).catch(err => ({ error: err.message }))
        )
      );
      const failed = results.filter(r => r?.error);
      if (failed.length) {
        alert(`${failed.length} of ${results.length} updates failed.`);
      } else {
        resetBulkDrafts();
      }
    } catch (err) {
      console.error(err);
      alert('Bulk update error');
    } finally {
      setIsBulkApplying(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRowIds.length) return;
    if (!window.confirm(`Delete ${selectedRowIds.length} selected route assignment(s)?`)) return;

    setIsBulkDeleting(true);
    try {
      const ids = [...selectedRowIds];
      const results = await Promise.all(
        ids.map(id =>
          fetch(`${API_URL}/assigned-routes/${id}`, { method: 'DELETE' })
            .then(r => r.json())
            .catch(err => ({ success: false, error: err.message }))
        )
      );
      const successIds = ids.filter((id, i) => results[i]?.success);
      setAssignedRoutes(prev => prev.filter(r => !successIds.includes(r._id)));
      setSelectedRowIds([]);
      resetBulkDrafts();
      const failed = results.filter(r => !r.success);
      if (failed.length) alert(`${failed.length} delete(s) failed.`);
    } catch (err) {
      console.error(err);
      alert('Bulk delete error');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // --- DYNAMIC REGION DATABASE CRUD OPERATIONS ---
  const handleCreateNewRegion = async () => {
    const name = newRegionName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_URL}/regions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const json = await res.json();
      if (json.success && json.data) {
        setRegions(prev => [...prev, json.data].sort((a, b) => a.name.localeCompare(b.name)));
        setNewRegionName('');
      } else {
        alert(json.message || 'Failed to create region.');
      }
    } catch (err) {
      console.error('Error creating region:', err);
      alert('Network error while creating region.');
    }
  };

  const handleSaveEditRegion = async (regionId) => {
    if (!editingRegionName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/regions/${regionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingRegionName.trim() })
      });
      const json = await res.json();
      if (json.success) {
        const oldRegion = regions.find(r => (r._id || r.id) === regionId);
        const oldName = oldRegion?.name;
        const newName = editingRegionName.trim();

        // Update regions state
        setRegions(prev => prev.map(r => (r._id || r.id) === regionId ? { ...r, name: newName } : r));

        // Cascade rename locally in transit levels
        setTransitLevels(prev => prev.map(tl => ({
          ...tl,
          regions: (tl.regions || []).map(reg => reg === oldName ? newName : reg)
        })));

        // Cascade rename locally in assigned routes
        setAssignedRoutes(prev => prev.map(ar => ar.deliveryRegion === oldName ? { ...ar, deliveryRegion: newName } : ar));

        setEditingRegionId(null);
        setEditingRegionName('');
      } else {
        alert(json.message || 'Failed to update region.');
      }
    } catch (err) {
      console.error('Error updating region:', err);
      alert('Network error while updating region.');
    }
  };

  const handleDeleteRegion = async (regionId, regionName) => {
    if (!window.confirm(`Delete region "${regionName}"? It will be automatically unassigned from any Transit Levels and Routes.`)) return;
    try {
      const res = await fetch(`${API_URL}/regions/${regionId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setRegions(prev => prev.filter(r => (r._id || r.id) !== regionId));

        // Unassign from transit levels locally
        setTransitLevels(prev => prev.map(tl => ({
          ...tl,
          regions: (tl.regions || []).filter(reg => reg !== regionName)
        })));

        // Unassign from assigned routes locally
        setAssignedRoutes(prev => prev.map(ar => ar.deliveryRegion === regionName ? { ...ar, deliveryRegion: null } : ar));
      } else {
        alert(json.message || 'Failed to delete region.');
      }
    } catch (err) {
      console.error('Error deleting region:', err);
      alert('Network error while deleting region.');
    }
  };

  // --- Transit Times Management Functions (INDIVIDUAL ITEM CRUD) ---
  const updateIndividualTransitLevelBackend = async (levelId, patch) => {
    setSavingTransitId(levelId);
    try {
      const res = await fetch(`${API_URL}/transit-times/${levelId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      const json = await res.json();
      if (!json.success) {
        alert(json.message || 'Failed to update transit level');
        return false;
      }
      return json.data;
    } catch (err) {
      console.error('Failed to update transit level with server:', err);
      alert('Network error while updating transit level');
      return false;
    } finally {
      setSavingTransitId(null);
    }
  };

  // Assign or Reassign regions (ensuring 1 active transit level per region)
  const handleTransitRegionsChange = async (levelId, selectedOptions) => {
    const newRegionValues = (selectedOptions || []).map(o => o.value);

    // Update local state immediately
    setTransitLevels(prevLevels =>
      prevLevels.map(item => {
        const rowId = item._id || item.id;
        if (rowId === levelId) {
          return { ...item, regions: newRegionValues };
        }
        return {
          ...item,
          regions: (item.regions || []).filter(r => !newRegionValues.includes(r))
        };
      })
    );

    // Save ONLY this individual transit level to the server
    await updateIndividualTransitLevelBackend(levelId, { regions: newRegionValues });
  };

  // Local state update while typing
  const handleTransitFieldChange = (levelId, field, value) => {
    setTransitLevels(prevLevels =>
      prevLevels.map(item => {
        const rowId = item._id || item.id;
        if (rowId === levelId) {
          return {
            ...item,
            [field]: field === 'calendarDays' ? (value === '' ? '' : Number(value)) : value
          };
        }
        return item;
      })
    );
  };

  // Persist individual field on blur
  const handleTransitFieldBlur = async (levelId, field, value) => {
    if (value === '' || value === undefined) return;
    const patch = { [field]: field === 'calendarDays' ? Number(value) : value };
    await updateIndividualTransitLevelBackend(levelId, patch);
  };

  // Create single transit level
  const handleAddTransitLevel = async () => {
    if (!newTransitLevelName.trim()) {
      alert('Please enter a Transit Level name (e.g. T-9)');
      return;
    }
    const daysVal = parseInt(newTransitDays, 10);
    if (isNaN(daysVal) || daysVal < 0) {
      alert('Please enter valid calendar days (0 or greater).');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/transit-times`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: newTransitLevelName.trim().toUpperCase(),
          calendarDays: daysVal,
          regions: []
        })
      });
      const json = await res.json();
      if (json.success && json.data) {
        const created = { ...json.data, id: json.data._id || json.data.id };
        setTransitLevels(prev =>
          [...prev, created].sort((a, b) => (Number(a.calendarDays) || 0) - (Number(b.calendarDays) || 0))
        );
        setNewTransitLevelName('');
        setNewTransitDays('');
      } else {
        alert(json.message || 'Failed to create transit level');
      }
    } catch (err) {
      console.error('Error creating transit level:', err);
      alert('Network error while creating transit level');
    }
  };

  // Delete single transit level
  const handleDeleteTransitLevel = async (levelId) => {
    const target = transitLevels.find(l => (l._id || l.id) === levelId);
    if (!window.confirm(`Delete transit level "${target?.level}"? Any assigned regions will be unassigned.`)) return;

    try {
      const res = await fetch(`${API_URL}/transit-times/${levelId}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (json.success) {
        setTransitLevels(prev => prev.filter(l => (l._id || l.id) !== levelId));
      } else {
        alert(json.message || 'Failed to delete transit level');
      }
    } catch (err) {
      console.error('Error deleting transit level:', err);
      alert('Network error while deleting transit level');
    }
  };

  if (pageLoading) return <GovernanceLoader message="Initializing Governance Layer..." />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 shadow-sm">
              <Settings className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-slate-800 truncate">Data Governance Layer</h1>
              <p className="text-[11px] sm:text-[14px] text-slate-400 truncate">Manage business rules, mappings, and consolidations.</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 py-4 sm:py-6">
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-[600px]">
          <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto no-scrollbar rounded-t-xl">
            {['customers', 'products', 'formats'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors uppercase whitespace-nowrap flex-shrink-0 ${activeTab === tab ? 'bg-white border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {tab === 'customers' && <Users size={16} className="sm:w-[18px] sm:h-[18px]" />}
                {tab === 'products' && <Package size={16} className="sm:w-[18px] sm:h-[18px]" />}
                {tab === 'formats' && <Layers size={16} className="sm:w-[18px] sm:h-[18px]" />}
                {tab} Families
              </button>
            ))}
            <button
              key="route"
              onClick={() => setActiveTab('route')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors uppercase whitespace-nowrap flex-shrink-0 ${
                activeTab === 'route'
                  ? 'bg-white border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Truck size={16} className="sm:w-[18px] sm:h-[18px]" />
              Assign Route
            </button>
          </div>

          <div className="p-4 sm:p-6 flex-1 bg-white">
            {activeTab === 'customers' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Consolidations</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Group multiple accounts under one parent family.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <input type="text" placeholder="New Family Name..." value={newFamilyName} onChange={(e) => setNewFamilyName(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none w-full sm:w-auto" />
                    <button onClick={handleAddCustomerFamily} className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 w-full sm:w-auto">
                      <Plus size={16} /> Create
                    </button>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {customerFamilies.map(family => (
                    <div key={family._id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                      <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-2 gap-2">
                        <h4 className="font-semibold text-gray-800 uppercase truncate min-w-0" title={family.name}>{family.name}</h4>
                        <button onClick={() => handleDeleteCustomerFamily(family._id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-md flex-shrink-0"><Trash2 size={16} /></button>
                      </div>
                      <div className="mb-4">
                        <div className="relative mb-3">
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={searchTerms[family._id] || ""}
                            onChange={(e) => setSearchTerms({
                              ...searchTerms,
                              [family._id]: e.target.value
                            })}
                            className="w-full pl-8 pr-3 py-1.5 text-xs border rounded-md focus:ring-1 focus:ring-indigo-500 outline-none bg-slate-50"
                          />
                          <svg className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>

                        <div className="max-h-[220px] overflow-y-auto pr-1 custom-scrollbar overscroll-contain">
                          <ul className="space-y-2">
                            {(family.customers || [])
                              .filter(c => !searchTerms[family._id] || c.toLowerCase().includes(searchTerms[family._id].toLowerCase()))
                              .map(c => (
                                <li key={c} className="flex justify-between items-center bg-white px-3 py-2 border rounded-md text-sm shadow-sm hover:border-indigo-200 transition-colors gap-2">
                                  <span className="truncate mr-2 font-medium text-slate-700 min-w-0" title={c}>{c}</span>
                                  <button
                                    onClick={() => handleRemoveCustomer(family._id, c)}
                                    className="text-gray-400 hover:text-red-500 font-bold px-1 flex-shrink-0"
                                  >
                                    &times;
                                  </button>
                                </li>
                              ))}

                            {family.customers?.length > 0 && family.customers.filter(c => !searchTerms[family._id] || c.toLowerCase().includes(searchTerms[family._id].toLowerCase())).length === 0 && (
                              <div className="text-center py-4 text-xs text-slate-400 italic bg-slate-50 rounded-md border border-dashed">
                                No matches found
                              </div>
                            )}
                          </ul>
                        </div>
                      </div>

                      {activeFamilyId === family._id ? (
                        <div className="space-y-2">
                          <AsyncSelect
                            key={family._id}
                            cacheOptions
                            defaultOptions
                            loadOptions={searchCustomerOptions}
                            inputValue={searchQuery}
                            onInputChange={(newValue, { action }) => {
                              if (action === "input-change") {
                                setSearchQuery(newValue);
                                lastSearchRef.current = newValue;
                                return newValue;
                              }
                              return lastSearchRef.current;
                            }}
                            isMulti
                            value={selectedCustomers}
                            closeMenuOnSelect={false}
                            blurInputOnSelect={false}
                            hideSelectedOptions={false}
                            components={{
                              Option: CheckboxOption,
                              MenuList: (props) => (
                                <div style={{ background: 'white' }}>
                                  <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    <div {...props}>{props.children}</div>
                                  </div>
                                  <div style={{ borderTop: '1px solid #eee', padding: '8px 12px', textAlign: 'right' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (document.activeElement instanceof HTMLElement) {
                                          document.activeElement.blur();
                                        }
                                      }}
                                      style={{
                                        padding: '4px 12px',
                                        backgroundColor: '#4f46e5',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Done
                                    </button>
                                  </div>
                                </div>
                              )
                            }}
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{
                              ...customerMultiSelectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: '250px',
                                overflowY: 'auto'
                              })
                            }}
                            onChange={setSelectedCustomers}
                            isOptionDisabled={(option) =>
                              option.isInFamily || (family.customers || []).includes(option.value)
                            }
                          />
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              onClick={() => handleAddCustomerToFamily(family._id)}
                              disabled={savingFamilyId === family._id || selectedCustomers.length === 0}
                              className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-indigo-700 disabled:bg-indigo-300"
                            >
                              {savingFamilyId === family._id && <Loader2 size={12} className="animate-spin" />}
                              Assign
                            </button>
                            <button
                              onClick={() => {
                                setActiveFamilyId(null);
                                setSelectedCustomers([]);
                                setSearchQuery("");
                              }}
                              className="text-gray-500 text-xs px-2 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setActiveFamilyId(family._id);
                            setSelectedCustomers([]);
                            setSearchQuery("");
                          }}
                          className="text-xs text-indigo-600 font-semibold hover:text-indigo-800"
                        >
                          + Assign Customer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Product Line Mapping</h3>
                    <p className="mt-1 text-xs sm:text-sm text-gray-500">Expand a family to review its SKUs.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
                    <div className="relative w-full sm:w-72">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        placeholder="Search Family or SKU..."
                        value={productFamilySearch}
                        onChange={(e) => { setProductFamilySearch(e.target.value); setProductFamilyPage(1); }}
                        className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm outline-none"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="New Product Family..."
                      value={newFamilyName}
                      onChange={(e) => setNewFamilyName(e.target.value)}
                      className="w-full sm:w-60 rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                    />
                    <button onClick={handleAddProductFamily} className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700 w-full sm:w-auto">+ Create</button>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 relative min-h-[300px]">
                  {isPfLoading && (
                    <div className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center backdrop-blur-[1px] rounded-lg">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="animate-spin text-indigo-600" size={32} />
                        <span className="text-xs font-medium text-indigo-600">Loading families...</span>
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm min-w-[600px]">
                      <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="w-12 px-4 py-3"></th>
                          <th className="px-4 py-3">Product Family</th>
                          <th className="w-32 px-4 py-3"># of SKUs</th>
                          <th className="w-48 px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedProductFamilyRows.map((family) => {
                          const isExpanded = expandedProductFamilyId === family._id;
                          return (
                            <React.Fragment key={family._id}>
                              <tr className="bg-white hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <button onClick={() => setExpandedProductFamilyId(isExpanded ? null : family._id)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md">
                                    <ChevronDown size={18} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                  </button>
                                </td>
                                <td className="px-4 py-3 font-semibold text-gray-900 uppercase">{family.name}</td>
                                <td className="px-4 py-3"><span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">{family.skuCount} SKUs</span></td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <button onClick={() => { setActiveFamilyId(family._id); setExpandedProductFamilyId(family._id); }} className="text-indigo-600 px-2 py-1.5 text-sm font-medium hover:bg-indigo-50 rounded-md">+ Assign</button>
                                  <button onClick={() => handleDeleteProductFamily(family._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md ml-2"><Trash2 size={16} /></button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-slate-50/50">
                                  <td colSpan="4" className="px-2 sm:px-4 py-4">
                                    <div className="space-y-4">
                                      {activeFamilyId === family._id && (
                                        <div className="p-3 bg-white border border-indigo-100 rounded-lg space-y-3 shadow-sm">
                                          <AsyncSelect
                                            value={selectedProducts}
                                            cacheOptions
                                            loadOptions={searchProductOptions}
                                            isMulti
                                            components={{ Option: CheckboxOption }}
                                            styles={{
                                              ...customerMultiSelectStyles,
                                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                            menuPortalTarget={document.body}
                                            isOptionDisabled={(o) => o.isAssigned}
                                            onChange={setSelectedProducts}
                                          />
                                          <div className="flex flex-col sm:flex-row justify-end gap-2">
                                            <button onClick={() => handleAddSkuToFamily(family._id)} className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm order-1 sm:order-2">Map Products</button>
                                            <button onClick={() => { setActiveFamilyId(null); setSelectedProducts([]); }} className="text-gray-500 text-sm px-2 py-2 order-2 sm:order-1">Cancel</button>
                                          </div>
                                        </div>
                                      )}

                                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center px-1 gap-2">
                                        <h4 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Currently Mapped</h4>
                                        <div className="relative w-full sm:w-64">
                                          <input
                                            type="text"
                                            placeholder="Filter SKUs within this family..."
                                            value={skuSearchTerms[family._id] || ""}
                                            onChange={(e) => setSkuSearchTerms({ ...skuSearchTerms, [family._id]: e.target.value })}
                                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-500 outline-none bg-white"
                                          />
                                          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        </div>
                                      </div>

                                      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
                                        <div className="max-h-[350px] overflow-y-auto overflow-x-auto custom-scrollbar overscroll-contain">
                                          <table className="w-full text-left text-sm relative min-w-[720px]">
                                            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 border-b border-gray-100 sticky top-0 z-10">
                                              <tr>
                                                <th className="px-4 py-2">SKU</th>
                                                <th className="px-4 py-2">Description</th>
                                                <th className="px-4 py-2">Category</th>
                                                <th className="px-4 py-2">Category Family</th>
                                                <th className="px-4 py-2">Format</th>
                                                <th className="w-10 px-4 py-2 text-right"></th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {family.skuRows
                                                .filter(row => {
                                                  const term = (skuSearchTerms[family._id] || "").toLowerCase();
                                                  return !term ||
                                                        row.sku.toLowerCase().includes(term) ||
                                                        (row.description || "").toLowerCase().includes(term);
                                                })
                                                .map(row => (
                                                <tr key={row.sku} className="hover:bg-gray-50 transition-colors">
                                                  <td className="px-4 py-2 font-mono text-indigo-600 font-medium text-xs">{row.sku}</td>
                                                  <td className="px-4 py-2 text-gray-600 text-[11px] leading-tight">{row.description}</td>
                                                  <td className="px-4 py-2 text-[10px] uppercase text-gray-400">{row.category}</td>
                                                  <td className="px-4 py-2 text-[10px] uppercase text-gray-400">{row.c_family ?? 'N/A'}</td>
                                                  <td className="px-4 py-2 text-[10px] font-bold text-slate-700">{row.c_format ?? 'N/A' }</td>
                                                  <td className="px-4 py-2 text-right">
                                                    <button
                                                      onClick={() => handleRemoveSku(family._id, row.sku)}
                                                      className="text-gray-400 hover:text-red-500 text-lg p-1"
                                                    >
                                                      &times;
                                                    </button>
                                                  </td>
                                                </tr>
                                              ))}

                                              {family.skuRows.length > 0 && family.skuRows.filter(row => {
                                                  const term = (skuSearchTerms[family._id] || "").toLowerCase();
                                                  return !term || row.sku.toLowerCase().includes(term) || (row.description || "").toLowerCase().includes(term);
                                              }).length === 0 && (
                                                <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400 italic">No SKUs match your filter criteria.</td></tr>
                                              )}

                                              {family.skuRows.length === 0 && (
                                                <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400 italic">No SKUs mapped to this family.</td></tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between rounded-b-lg">
                    <div className="text-xs sm:text-sm">Showing {totalProductFamilies === 0 ? 0 : productFamilyPageStart + 1}-{productFamilyPageEnd} of {totalProductFamilies} families</div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={productFamilyPageSize} onChange={(e) => { setProductFamilyPageSize(Number(e.target.value)); setProductFamilyPage(1); }} className="rounded-md border px-2 py-1 outline-none text-xs">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s} rows</option>)}
                      </select>
                      <button disabled={productFamilyPage <= 1} onClick={() => setProductFamilyPage(p => p - 1)} className="px-3 py-1 border bg-white rounded-md disabled:opacity-50 text-xs">Prev</button>
                      <span className="px-2 text-xs font-medium whitespace-nowrap">Page {productFamilyPage} of {productFamilyPageCount}</span>
                      <button disabled={productFamilyPage >= productFamilyPageCount} onClick={() => setProductFamilyPage(p => p + 1)} className="px-3 py-1 border bg-white rounded-md disabled:opacity-50 text-xs">Next</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'formats' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">ERPAG Category Mapping</h3>
                    <p className="text-xs text-gray-500">Assign category families and Retail/Bulk format types to ERPAG categories.</p>
                  </div>
                  <span className="text-xs sm:text-sm text-gray-500">{formatTotalCount} Categories</span>
                </div>

                <div className="bg-[#f9fafb] p-4 rounded-t-lg border border-gray-200 flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 w-full">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">ERPAG Category</label>
                    <input
                      type="text"
                      placeholder="e.g. A Spice Affair 12-CS"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-indigo-500 outline-none bg-white h-10"
                    />
                  </div>

                  <div className="w-full sm:w-64">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Format Type</label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-indigo-500 outline-none bg-white h-10"
                    >
                      <option value="">All Types</option>
                      {FORMAT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>

                  {(searchTerm || typeFilter) ? (
                    <button
                      onClick={() => { setSearchTerm(''); setTypeFilter(''); }}
                      className="h-10 px-4 flex items-center justify-center gap-2 text-[11px] font-bold text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 rounded-md transition-all border border-transparent whitespace-nowrap w-full sm:w-auto"
                    >
                      <X size={14} strokeWidth={3} />
                      RESET
                    </button>
                  ) : (
                    <div className="hidden sm:block w-[90px] h-10"></div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-b-lg border-t-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left min-w-[720px]">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase border-b">
                        <tr>
                          <th className="px-4 py-4 w-12 text-center">#</th>
                          <th className="px-4 py-4">ERPAG Category</th>
                          <th className="px-4 py-4">Category Family</th>
                          <th className="px-4 py-4">Format Type</th>
                          <th className="w-32 px-4 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedFormatRows.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-12 text-center">
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <p className="text-base font-medium">No format rows available.</p>
                                <p className="text-xs">Try adjusting your search or filter settings.</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          paginatedFormatRows.map((row, index) => (
                            <tr key={row.key} className="hover:bg-gray-50/50">
                              <td className="px-4 py-4 text-center">{index + 1}</td>
                              <td className="px-4 py-4">
                                <div className="font-medium text-gray-900">{row.category}</div>
                                {row.hasSavedMapping && <span className="text-[10px] text-green-600 font-medium">Mapped</span>}
                              </td>
                              <td className="px-4 py-4">
                                <input
                                  type="text"
                                  placeholder="e.g. Garlic, Jar, Pouch"
                                  value={row.draftFamily}
                                  onChange={e => handleFormatDraftChange(row.category, 'categoryFamily', e.target.value)}
                                  className="w-full min-w-[140px] border border-gray-200 rounded px-3 py-2 outline-none focus:border-indigo-500 text-sm"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <select
                                  value={row.draftFormatType}
                                  onChange={e => handleFormatDraftChange(row.category, 'formatType', e.target.value)}
                                  className="w-full min-w-[120px] border border-gray-200 rounded px-3 py-2 outline-none text-sm"
                                >
                                  <option value="">Select type</option>
                                  {FORMAT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              </td>
                              <td className="px-4 py-4 text-right whitespace-nowrap">
                                <button onClick={() => handleSaveFormatMapping(row.category)} disabled={!row.isDirty || savingFormatCategory === row.key} className="p-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-200">
                                  {savingFormatCategory === row.key ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                </button>
                                <button onClick={() => handleDeleteFormat(row.savedMapping._id, row.category)} disabled={!row.hasSavedMapping} className="p-2 text-red-500 hover:bg-red-50 rounded-md disabled:opacity-20 ml-2"><Trash2 size={16} /></button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {formatTotalCount > 0 && (
                    <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between rounded-b-lg">
                      <div className="text-xs sm:text-sm">
                        Showing {formatPageStart + 1}-{formatPageEnd} of {formatTotalCount} categories
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={formatPageSize}
                          onChange={(e) => { setFormatPageSize(Number(e.target.value)); setFormatPage(1); }}
                          className="rounded-md border px-2 py-1 outline-none text-xs"
                        >
                          {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s} rows</option>)}
                        </select>
                        <button disabled={formatPage <= 1} onClick={() => setFormatPage(p => p - 1)} className="px-3 py-1 border bg-white rounded-md disabled:opacity-50 text-xs">Prev</button>
                        <span className="px-2 text-xs whitespace-nowrap">Page {formatPage} of {formatPageCount}</span>
                        <button disabled={formatPage >= formatPageCount} onClick={() => setFormatPage(p => p + 1)} className="px-3 py-1 border bg-white rounded-md disabled:opacity-50 text-xs">Next</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'route' && (
              <div className="p-4 sm:p-6 max-w-6xl bg-white border border-gray-200 rounded-lg shadow-sm space-y-10">

                {/* --- Section 1: Route Assignment Form --- */}
                <div>
                  <div className="mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Assign Routes</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      Map delivery routes, delivery regions, and shipping rules to consolidated families or individual customers. Select multiple targets to batch-assign.
                    </p>
                  </div>

                  <div className="bg-gray-50/50 p-4 sm:p-5 rounded-lg border border-gray-100 mb-8">
                    <div className="mb-5">
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-3 tracking-wider">Assignment Type</label>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="assignType" value="family" checked={routeAssignmentType === 'family'} onChange={() => { setRouteAssignmentType('family'); setSelectedRouteTargets([]); }} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm font-medium text-gray-700">Customer Family</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="assignType" value="customer" checked={routeAssignmentType === 'customer'} onChange={() => { setRouteAssignmentType('customer'); setSelectedRouteTargets([]); }} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                          <span className="text-sm font-medium text-gray-700">Individual Customer</span>
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">
                          Select {routeAssignmentType === 'family' ? 'Families' : 'Customers'} (multi)
                        </label>
                        {routeAssignmentType === 'family' ? (
                          <Select isMulti options={routeTargetOptions} value={selectedRouteTargets} onChange={(val) => setSelectedRouteTargets(val || [])} placeholder="Search Families..." isClearable isSearchable closeMenuOnSelect={false} menuPortalTarget={document.body} styles={{ ...customerMultiSelectStyles, menuPortal: (b) => ({ ...b, zIndex: 9999 }) }} />
                        ) : (
                          <AsyncSelect isMulti cacheOptions defaultOptions={activeCustomerOptions} loadOptions={searchCustomerOptions} value={selectedRouteTargets} onChange={(val) => setSelectedRouteTargets(val || [])} placeholder="Search Customers..." isClearable closeMenuOnSelect={false} hideSelectedOptions={false} menuPortalTarget={document.body} styles={{ ...customerMultiSelectStyles, menuPortal: (b) => ({ ...b, zIndex: 9999 }) }} />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Assign Route</label>
                        <Select options={ROUTE_OPTIONS} value={selectedRoute} onChange={setSelectedRoute} placeholder="Select Route..." isClearable isSearchable menuPortalTarget={document.body} styles={{ ...categorySelectStyles, menuPortal: (b) => ({ ...b, zIndex: 9999 }) }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Delivery Region (Loaded from DB)</label>
                        <Select options={dynamicRegionOptions} value={routeDeliveryRegion} onChange={setRouteDeliveryRegion} placeholder="Select Region..." isClearable isSearchable menuPortalTarget={document.body} styles={{ ...categorySelectStyles, menuPortal: (b) => ({ ...b, zIndex: 9999 }) }} />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Default Lead Time (days)</label>
                        <input type="number" min="0" value={routeLeadTime} onChange={(e) => setRouteLeadTime(e.target.value)} placeholder="e.g. 3" className="w-full h-[38px] border border-gray-300 rounded-lg px-3 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Warehouse Allocation</label>
                        <Select options={warehouseOptions} value={routeWarehouse} onChange={setRouteWarehouse} placeholder={warehousesLoading ? 'Loading warehouses...' : 'Select Warehouse...'} isClearable isSearchable isLoading={warehousesLoading} menuPortalTarget={document.body} styles={{ ...categorySelectStyles, menuPortal: (b) => ({ ...b, zIndex: 9999 }) }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                      {[
                        { label: 'Has Cancel Date', val: routeHasCancelDate, setter: setRouteHasCancelDate },
                        { label: 'Requires Delivery Appointment', val: routeRequiresAppointment, setter: setRouteRequiresAppointment },
                        { label: 'Requires Customs Docs', val: routeRequiresCustoms, setter: setRouteRequiresCustoms },
                      ].map(({ label, val, setter }) => (
                        <div key={label} className="bg-white border border-gray-200 rounded-lg p-3">
                          <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">{label}</label>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setter(true)} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${val ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>YES</button>
                            <button type="button" onClick={() => setter(false)} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${!val ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>NO</button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end pt-2">
                      <button onClick={handleSaveRouteAssignment} disabled={!selectedRouteTargets.length || !selectedRoute || isSavingRoute} className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors w-full sm:w-auto">
                        {isSavingRoute ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        Assign Route{selectedRouteTargets.length > 1 ? `s to ${selectedRouteTargets.length} Selected` : ''}
                      </button>
                    </div>
                  </div>

                  {/* Current Assignments Table */}
                  <div className="mt-8 border-t border-gray-200 pt-6">
                    <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <h4 className="text-md font-semibold text-gray-900">Current Route Assignments</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Edit any field directly. Select rows, set bulk values in headers, then click <strong>Apply Bulk Changes</strong> once.
                        </p>
                      </div>
                      {selectedRowIds.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                            {selectedRowIds.length} selected
                          </span>
                          <button
                            onClick={() => { setSelectedRowIds([]); resetBulkDrafts(); }}
                            className="text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1"
                          >
                            Clear
                          </button>
                          <button
                            onClick={applyBulkChanges}
                            disabled={!hasBulkChanges || isBulkApplying}
                            className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 whitespace-nowrap"
                          >
                            {isBulkApplying ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            Apply Bulk Changes
                          </button>
                          <button
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                            className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-red-700 disabled:bg-red-300 whitespace-nowrap"
                          >
                            {isBulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            Delete Selected
                          </button>
                        </div>
                      )}
                    </div>

                    {assignedRoutes.length === 0 ? (
                      <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <p className="text-sm text-gray-500 italic">No routes currently assigned.</p>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg bg-white">
                        <div className="max-h-[500px] overflow-y-auto overflow-x-auto custom-scrollbar overscroll-contain">
                          <table className="w-full text-left text-sm relative min-w-[1350px]">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-200 sticky top-0 z-10">
                              <tr>
                                <th className="px-3 py-3 w-10 text-center align-top">
                                  <input
                                    type="checkbox"
                                    checked={selectedRowIds.length === assignedRoutes.length && assignedRoutes.length > 0}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 text-indigo-600 rounded border-gray-300 cursor-pointer mt-0.5"
                                  />
                                </th>
                                <th className="px-3 py-3 w-24 align-top">Type</th>
                                <th className="px-3 py-3 align-top">Name</th>

                                <th className="px-3 py-3 w-40 align-top">
                                  <div>Route</div>
                                  {selectedRowIds.length > 0 && (
                                    <select
                                      value={bulkRouteValue}
                                      onChange={(e) => setBulkRouteValue(e.target.value)}
                                      className="mt-1.5 w-full border border-indigo-200 rounded px-1.5 py-1 text-[10px] bg-white outline-none normal-case"
                                    >
                                      <option value="">No change</option>
                                      {ROUTE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  )}
                                </th>

                                <th className="px-3 py-3 w-36 align-top">
                                  <div>Region</div>
                                  {selectedRowIds.length > 0 && (
                                    <select
                                      value={bulkDeliveryRegionValue}
                                      onChange={(e) => setBulkDeliveryRegionValue(e.target.value)}
                                      className="mt-1.5 w-full border border-indigo-200 rounded px-1.5 py-1 text-[10px] bg-white outline-none normal-case"
                                    >
                                      <option value="">No change</option>
                                      {dynamicRegionOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                  )}
                                </th>

                                <th className="px-3 py-3 w-24 align-top">
                                  <div>Lead (d)</div>
                                  {selectedRowIds.length > 0 && (
                                    <input
                                      type="number"
                                      min="0"
                                      value={bulkLeadTimeValue}
                                      onChange={(e) => setBulkLeadTimeValue(e.target.value)}
                                      placeholder="No change"
                                      className="mt-1.5 w-full border border-indigo-200 rounded px-1.5 py-1 text-[10px] bg-white outline-none normal-case"
                                    />
                                  )}
                                </th>

                                <th className="px-3 py-3 w-40 align-top">
                                  <div>Warehouse</div>
                                  {selectedRowIds.length > 0 && (
                                    <select
                                      value={bulkWarehouseValue}
                                      onChange={(e) => setBulkWarehouseValue(e.target.value)}
                                      className="mt-1.5 w-full border border-indigo-200 rounded px-1.5 py-1 text-[10px] bg-white outline-none normal-case"
                                    >
                                      <option value="">No change</option>
                                      {warehouseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  )}
                                </th>

                                {[
                                  { label: 'Cancel', value: bulkHasCancelDate, setter: setBulkHasCancelDate },
                                  { label: 'Appt', value: bulkRequiresAppointment, setter: setBulkRequiresAppointment },
                                  { label: 'Customs', value: bulkRequiresCustoms, setter: setBulkRequiresCustoms },
                                ].map(col => (
                                <th key={col.label} className="px-3 py-3 w-20 text-center align-top">
                                  <div>{col.label}</div>
                                  {selectedRowIds.length > 0 && (
                                    <select
                                      value={col.value}
                                      onChange={(e) => col.setter(e.target.value)}
                                      className="mt-1.5 w-full border border-indigo-200 rounded px-1 py-1 text-[10px] bg-white outline-none normal-case"
                                    >
                                      <option value="">—</option>
                                      <option value="true">YES</option>
                                      <option value="false">NO</option>
                                    </select>
                                  )}
                                </th>
                                ))}

                                <th className="px-3 py-3 w-12 text-center align-top">Del</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {assignedRoutes.map(item => {
                                const isSelected = selectedRowIds.includes(item._id);
                                const leadDraft = leadTimeDrafts[item._id];
                                const leadVal = leadDraft !== undefined ? leadDraft : (item.leadTime ?? '');
                                return (
                                  <tr key={item._id} className={`transition-colors ${isSelected ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}`}>
                                    <td className="px-3 py-3 text-center">
                                      <input type="checkbox" checked={isSelected} onChange={() => toggleRowSelection(item._id)} className="w-4 h-4 text-indigo-600 rounded border-gray-300 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-3">
                                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold tracking-wider whitespace-nowrap ${item.assignType === 'customer' ? 'bg-sky-100 text-sky-700' : 'bg-purple-100 text-purple-700'}`}>
                                        {item.assignType === 'customer' ? 'CUSTOMER' : 'FAMILY'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 font-medium text-gray-900">{item.name}</td>

                                    <td className="px-3 py-3">
                                      <div className="flex items-center gap-1">
                                        <select value={item.routeName || ''} onChange={(e) => handleCellUpdate(item, 'routeName', e.target.value)} className="w-full min-w-0 border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500">
                                          <option value="">Select</option>
                                          {ROUTE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                        {savingCellKey === `${item._id}:routeName` && <Loader2 size={12} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                      </div>
                                    </td>

                                    <td className="px-3 py-3">
                                      <div className="flex items-center gap-1">
                                        <select value={item.deliveryRegion || ''} onChange={(e) => handleCellUpdate(item, 'deliveryRegion', e.target.value)} className="w-full min-w-0 border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500">
                                          <option value="">Select</option>
                                          {dynamicRegionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                        {savingCellKey === `${item._id}:deliveryRegion` && <Loader2 size={12} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                      </div>
                                    </td>

                                    <td className="px-3 py-3">
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="number" min="0" value={leadVal}
                                          onChange={(e) => setLeadTimeDrafts(prev => ({ ...prev, [item._id]: e.target.value }))}
                                          onBlur={(e) => {
                                            const newVal = e.target.value;
                                            const currentVal = item.leadTime ?? '';
                                            if (String(newVal) !== String(currentVal)) handleCellUpdate(item, 'leadTime', newVal);
                                            setLeadTimeDrafts(prev => { const next = { ...prev }; delete next[item._id]; return next; });
                                          }}
                                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                          className="w-full min-w-0 border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500"
                                        />
                                        {savingCellKey === `${item._id}:leadTime` && <Loader2 size={12} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                      </div>
                                    </td>

                                    <td className="px-3 py-3">
                                      <div className="flex items-center gap-1">
                                        <select value={item.warehouse || ''} onChange={(e) => handleCellUpdate(item, 'warehouse', e.target.value)} className="w-full min-w-0 border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500">
                                          <option value="">Select</option>
                                          {warehouseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        {savingCellKey === `${item._id}:warehouse` && <Loader2 size={12} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                      </div>
                                    </td>

                                    {[
                                      { field: 'hasCancelDate' },
                                      { field: 'requiresDeliveryAppointment' },
                                      { field: 'requiresCustomsDocs' },
                                    ].map(({ field }) => {
                                      const val = toBool(item[field]);
                                      return (
                                        <td key={field} className="px-3 py-3 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <button
                                              type="button"
                                              onClick={() => handleCellUpdate(item, field, !val)}
                                              className={`px-2 py-1 rounded text-[10px] font-bold transition-colors ${val ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                              title="Click to toggle"
                                            >
                                              {val ? 'YES' : 'NO'}
                                            </button>
                                            {savingCellKey === `${item._id}:${field}` && <Loader2 size={10} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                          </div>
                                        </td>
                                      );
                                    })}

                                    <td className="px-3 py-3 text-center">
                                      <button onClick={() => handleDeleteAssignedRoute(item)} disabled={isDeletingRow === item._id} className="text-red-500 hover:bg-red-50 p-1.5 rounded-md disabled:opacity-50" title="Delete this assignment">
                                        {isDeletingRow === item._id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* --- Section 2: Transit Times & Dynamic Region Management --- */}
                <div className="border-t-2 border-gray-100 pt-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                    <div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Transit Times Management</h3>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        Define transit levels and calendar days. Assign multiple regions per level. Each transit level updates individually in the database.
                      </p>
                    </div>
                  </div>

                  {/* Add Transit Level and Add Region Toolbar */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    {/* Add Transit Level Box */}
                    <div className="lg:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Create New Transit Level</span>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          placeholder="e.g. T-9"
                          value={newTransitLevelName}
                          onChange={(e) => setNewTransitLevelName(e.target.value)}
                          className="w-full sm:w-36 h-9 px-3 text-xs border border-gray-300 rounded-md outline-none focus:border-indigo-500 bg-white"
                        />
                        <input
                          type="number"
                          min="0"
                          placeholder="Days (e.g. 9)"
                          value={newTransitDays}
                          onChange={(e) => setNewTransitDays(e.target.value)}
                          className="w-full sm:w-36 h-9 px-3 text-xs border border-gray-300 rounded-md outline-none focus:border-indigo-500 bg-white"
                        />
                        <button
                          type="button"
                          onClick={handleAddTransitLevel}
                          className="flex items-center justify-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-md text-xs font-semibold hover:bg-indigo-700 transition-colors"
                        >
                          <Plus size={14} /> Add Transit
                        </button>
                      </div>
                    </div>

                    {/* Add Dynamic Region Box */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Create New Dynamic Region</span>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="New Region Name..."
                          value={newRegionName}
                          onChange={(e) => setNewRegionName(e.target.value)}
                          className="w-full h-9 px-3 text-xs border border-gray-300 rounded-md outline-none focus:border-indigo-500 bg-white"
                        />
                        <button
                          type="button"
                          onClick={handleCreateNewRegion}
                          className="flex items-center justify-center gap-1 bg-slate-700 text-white px-3 py-2 rounded-md text-xs font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap"
                        >
                          <Plus size={14} /> Save
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Dynamic Regions List with In-Place Edit & Delete */}
                  <div className="mb-6 bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                      Regions ({regions.length} Loaded)
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {regions.map((reg) => {
                        const rId = reg._id || reg.id;
                        const isEditing = editingRegionId === rId;

                        return (
                          <div key={rId} className="flex items-center bg-white border border-gray-200 px-2.5 py-1.5 rounded-md shadow-sm gap-2">
                            {isEditing ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  value={editingRegionName}
                                  onChange={(e) => setEditingRegionName(e.target.value)}
                                  className="border border-indigo-400 rounded px-1.5 py-0.5 text-xs outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSaveEditRegion(rId)}
                                  className="text-green-600 hover:text-green-800 p-0.5"
                                  title="Save"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingRegionId(null)}
                                  className="text-gray-400 hover:text-gray-600 p-0.5"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="text-xs font-medium text-slate-800">{reg.name}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingRegionId(rId);
                                    setEditingRegionName(reg.name);
                                  }}
                                  className="text-gray-400 hover:text-indigo-600 p-0.5"
                                  title="Edit Region Name"
                                >
                                  <Edit2 size={12} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRegion(rId, reg.name)}
                                  className="text-gray-400 hover:text-red-500 p-0.5"
                                  title="Delete Region from DB"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Consolidated Transit Times Table (Updated Individually) */}
                  <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm min-w-[700px]">
                        <thead className="bg-gray-50 text-xs text-gray-600 uppercase border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3.5 w-44 font-semibold">Transit Level</th>
                            <th className="px-4 py-3.5 w-40 font-semibold">Calendar Days</th>
                            <th className="px-4 py-3.5 font-semibold">Assigned Regions (Multi-select)</th>
                            <th className="px-4 py-3.5 w-24 text-center font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {transitLevels.map((row) => {
                            const rowId = row._id || row.id;
                            const isSavingThisRow = savingTransitId === rowId;

                            // Region options for react-select with active assignment indicators
                            const currentRegionOptions = dynamicRegionOptions.map(opt => {
                              const assignedTo = transitLevels.find(
                                l => (l.regions || []).includes(opt.value) && (l._id || l.id) !== rowId
                              );
                              return {
                                value: opt.value,
                                label: assignedTo ? `${opt.value} (Currently: ${assignedTo.level})` : opt.value,
                              };
                            });

                            const selectedRegionValues = (row.regions || []).map(r => ({ value: r, label: r }));

                            return (
                              <tr key={rowId} className="hover:bg-slate-50/70 transition-colors">
                                {/* Transit Level Name (Individual onBlur update) */}
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="text"
                                      value={row.level || ''}
                                      onChange={(e) => handleTransitFieldChange(rowId, 'level', e.target.value.toUpperCase())}
                                      onBlur={(e) => handleTransitFieldBlur(rowId, 'level', e.target.value.toUpperCase())}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      className="w-24 font-bold text-indigo-700 border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500 uppercase"
                                    />
                                    {isSavingThisRow && <Loader2 size={12} className="animate-spin text-indigo-600 flex-shrink-0" />}
                                  </div>
                                </td>

                                {/* Calendar Days (Individual onBlur update) */}
                                <td className="px-4 py-3 align-middle">
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="0"
                                      value={row.calendarDays !== undefined ? row.calendarDays : ''}
                                      onChange={(e) => handleTransitFieldChange(rowId, 'calendarDays', e.target.value)}
                                      onBlur={(e) => handleTransitFieldBlur(rowId, 'calendarDays', e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      className="w-20 font-semibold border border-gray-200 rounded px-2 py-1.5 text-xs outline-none bg-white focus:border-indigo-500"
                                    />
                                    <span className="text-xs text-gray-500">days</span>
                                  </div>
                                </td>

                                {/* Multi-Select Assigned Regions (Individual onChange update) */}
                                <td className="px-4 py-3 align-middle">
                                  <Select
                                    isMulti
                                    options={currentRegionOptions}
                                    value={selectedRegionValues}
                                    onChange={(vals) => handleTransitRegionsChange(rowId, vals)}
                                    placeholder="Assign regions..."
                                    closeMenuOnSelect={false}
                                    menuPortalTarget={document.body}
                                    styles={{
                                      ...customerMultiSelectStyles,
                                      menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                                      valueContainer: (provided) => ({ ...provided, minHeight: '34px', padding: '1px 6px' }),
                                    }}
                                  />
                                </td>

                                {/* Delete Level Action (Individual delete) */}
                                <td className="px-4 py-3 text-center align-middle">
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTransitLevel(rowId)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete Transit Level"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}

                          {transitLevels.length === 0 && (
                            <tr>
                              <td colSpan="4" className="px-4 py-8 text-center text-gray-400 italic">
                                No transit levels defined. Add a transit level above.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}